import {
  GKButton,
  GKDatePicker,
  GKTextField,
  Grid2,
  SimpleLoader,
} from "components";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import {
  useGetBrokerDetailsQuery,
  useUpdateBrokerDetailsMutation,
} from "store/features/SettingsApi";
import { useAppSelector } from "store/store";
import { emailRegex } from "utils/validationSchema";

type FormValues = {
  appName: string;
  appSource: string;
  userId: string;
  password: string;
  userKey: string;
  encryptionKey: string;
  ocpKey: string;
  partnerCode: string;
  partnerPassword: string;
  partnerDob: string;
  partnerEmailId: string;
  partnerContactNumber: string;
  company?: number;
};

const IIFLBrokerConfigure = () => {
  const userData = useAppSelector((state) => state?.user?.userData);

  const { data, isFetching } = useGetBrokerDetailsQuery(userData.companyId);
  const [updateBrokerDetails, { isLoading }] = useUpdateBrokerDetailsMutation();

  const form = useForm<FormValues>({
    defaultValues: {
      appName: "",
      appSource: "",
      userId: "",
      password: "",
      userKey: "",
      encryptionKey: "",
      ocpKey: "",
      partnerCode: "",
      partnerPassword: "",
      partnerDob: "",
      partnerEmailId: "",
      partnerContactNumber: "",
    },
    values: {
      appName: data?.data?.appName || "",
      appSource: data?.data?.appSource || "",
      userId: data?.data?.userId || "",
      password: data?.data?.password || "",
      userKey: data?.data?.userKey || "",
      encryptionKey: data?.data?.encryptionKey || "",
      ocpKey: data?.data?.ocpKey || "",
      partnerCode: data?.data?.partnerCode || "",
      partnerPassword: data?.data?.partnerPassword || "",
      partnerDob: data?.data?.partnerDob || "",
      partnerEmailId: data?.data?.partnerEmailId || "",
      partnerContactNumber: data?.data?.partnerContactNumber || "",
    },
  });

  if (isFetching) return <SimpleLoader />;

  return (
    <form
      onSubmit={form.handleSubmit(async (values) => {
        delete values.company;

        try {
          await updateBrokerDetails({
            id: userData.companyId,
            body: values,
          }).unwrap();
        } catch (error) {
          console.log(error);
        }
      })}
    >
      <Grid2 container spacing={2}>
        <Grid2 size={4}>
          <Controller
            name="appName"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="App Name" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="appSource"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="App Source" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="userId"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="User Id" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Password" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="userKey"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="User Key" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="encryptionKey"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Encryption Key" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="ocpKey"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="OCP Key" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="partnerCode"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Partner Code" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="partnerPassword"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Partner Password" />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="partnerDob"
            control={form.control}
            render={({ field }) => (
              <GKDatePicker
                {...field}
                inputLabel="Date Of Birth"
                value={dayjs(field.value)}
                onChange={(value) => {
                  field.onChange(dayjs(value).format("YYYY-MM-DD"));
                }}
                disableFuture
                slotProps={{
                  textField: {
                    error: false,
                    helperText: false,
                  },
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="partnerEmailId"
            control={form.control}
            rules={{
              pattern: {
                value: emailRegex,
                message: "Please enter valid email",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                inputLabel="Partner Email"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="partnerContactNumber"
            control={form.control}
            render={({ field }) => (
              <GKTextField {...field} inputLabel="Partner Contact Number" />
            )}
          />
        </Grid2>
        <Grid2 size={12} mt={2} display={"flex"} justifyContent={"flex-end"}>
          <GKButton variant="contained" type="submit" loading={isLoading}>
            Save
          </GKButton>
        </Grid2>
      </Grid2>
    </form>
  );
};

export default IIFLBrokerConfigure;
